import { motion } from 'motion/react'
import { format, getISOWeek, parseISO } from 'date-fns'
import type { DayDetailOut } from '../../api/types'
import { formatDuration } from '../../lib/format'
import { useUnits } from '../../lib/UnitsProvider'
import { TOKENS, Chip } from '../ui/dashboard-primitives'
import { SessionSummaryHeader } from './SessionSummaryHeader'
import { SessionSection } from './SessionSection'
import { HrZoneChart } from './HrZoneChart'
import {
  buildCardioModels,
  buildFreeWeightModels,
  buildMilonModels,
  buildSummary,
} from './sessionModel'

const EASE_OUT = [0.23, 1, 0.32, 1] as const
const MILON_BIKE_DEVICE_ID = 501

interface DayDetailProps {
  day: DayDetailOut
  isFetching?: boolean
}

function cardioSectionTotal(day: DayDetailOut): string {
  const totalKcal = Math.round(
    day.cardio.reduce((a, c) => a + (c.energy_kcal ?? c.total_calories ?? 0), 0)
  )
  const totalMin = Math.round(day.cardio.reduce((a, c) => a + (c.total_duration_sec ?? 0), 0) / 60)
  return `${totalKcal} kcal\n${totalMin} min`
}

function freeWeightSectionTotal(day: DayDetailOut, tonnage: (n: number) => string): string {
  // The server's block figure (api/training_volume.py): free weights plus the
  // Technogym machine sets merged in, both sides of a unilateral set counted.
  // It used to be summed here from the rows, one side at a time.
  const block = day.free_weight_workout
  const kg = block?.total_volume_kg ?? 0
  const count = block?.working_set_count ?? 0
  const heldSec = block?.total_seconds_held ?? 0
  // tonnage(), not kg.toLocaleString(): the bare call formats in the BROWSER's
  // locale, so on a German browser this section read "1.662,5 KG" directly
  // beneath a summary hero reading "1,663 kg" -- the same number, twice, two
  // ways, on one screen. The hero already pins en-US; this now agrees with it.
  const lines = [tonnage(kg), `${count} set${count === 1 ? '' : 's'}`]
  // C-D7: held time sits next to the set count it explains — seven of
  // nineteen sets carrying no kg makes sense once the time is beside it.
  if (heldSec > 0) lines.push(`${formatDuration(heldSec)} held`)
  return lines.join('\n')
}

function milonSectionTotal(day: DayDetailOut, tonnage: (n: number) => string): string {
  const kg = day.volume?.by_scale.milon.tonnage_kg ?? 0
  const qualities = (day.milon?.devices ?? [])
    .filter((d) => d.device_id !== MILON_BIKE_DEVICE_ID && d.quality != null)
    .map((d) => d.quality as number)
  if (qualities.length === 0) {
    return tonnage(kg)
  }
  const avgQuality = qualities.reduce((a, q) => a + q, 0) / qualities.length / 10
  return `${tonnage(kg)}\nquality ${avgQuality.toFixed(1)}`
}

/**
 * Combined detail for one training day: date header, then the unified
 * summary hero, then Cardio / Free Weights / Milon sections in that order.
 * Any part may be absent.
 */
export function DayDetail({ day, isFetching = false }: DayDetailProps) {
  const { tonnage } = useUnits()
  const date = parseISO(day.date)

  const cardio = buildCardioModels(day)
  const free = buildFreeWeightModels(day)
  const milon = buildMilonModels(day)
  const hasAny = cardio.length > 0 || free.length > 0 || milon.length > 0

  return (
    <motion.div
      animate={{ opacity: isFetching ? 0.55 : 1 }}
      transition={{ duration: 0.2 }}
      className="max-w-[1200px] mx-auto flex flex-col gap-10 lg:gap-12 pt-10 lg:pt-14"
    >
      <motion.header
        initial={{ opacity: 0, transform: 'translateY(8px)' }}
        animate={{ opacity: 1, transform: 'translateY(0px)' }}
        transition={{ duration: 0.5, ease: EASE_OUT }}
        className="flex flex-col gap-3"
      >
        <span
          className="inline-flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase"
          style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
        >
          <span style={{ color: TOKENS.amber }}>{format(date, 'EEE').toUpperCase()}</span>
          <span style={{ color: TOKENS.creamFaint }}>/</span>
          Week {getISOWeek(date)}
          <span style={{ color: TOKENS.creamFaint }}>/</span>
          {format(date, 'yyyy')}
        </span>
        <h2
          className="leading-[0.92]"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
            letterSpacing: '-0.034em',
            color: TOKENS.cream,
          }}
        >
          <span style={{ color: TOKENS.amber }}>{format(date, 'd')}</span>{' '}
          <span>{format(date, 'MMMM')}</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {day.milon && <Chip variant="amber">Strength</Chip>}
          {day.cardio.length > 0 && <Chip variant="default">Cardio</Chip>}
          {day.free_weight_workout && <Chip variant="default">Free Weights</Chip>}
        </div>
      </motion.header>

      {hasAny && <SessionSummaryHeader summary={buildSummary(day)} />}

      {day.live && <HrZoneChart live={day.live} />}

      {cardio.length > 0 && (
        <SessionSection
          title="Cardio"
          sub={`${cardio.length} machine${cardio.length > 1 ? 's' : ''} · Technogym`}
          total={cardioSectionTotal(day)}
          icon="cardio"
          items={cardio}
          baseDelayMs={60}
        />
      )}

      {free.length > 0 && (
        <SessionSection
          title="Free Weights"
          sub={`${free.length} exercise${free.length > 1 ? 's' : ''}`}
          total={freeWeightSectionTotal(day, tonnage)}
          icon="free"
          items={free}
          baseDelayMs={240}
        />
      )}

      {milon.length > 0 && (
        <SessionSection
          title="Milon Circuit"
          sub={`${milon.length} machine${milon.length > 1 ? 's' : ''} · ${day.milon?.rounds ?? 2} rounds`}
          total={milonSectionTotal(day, tonnage)}
          icon="milon"
          items={milon}
          baseDelayMs={420}
        />
      )}
    </motion.div>
  )
}
